/**
 * Persistent red overlay that renders whenever the current customer session
 * is an admin-view session (i.e. the SaaS owner-admin dropped into a
 * customer's workspace as themselves, silent to the customer).
 *
 * Different from ImpersonationBanner:
 *   - Colored RED (impersonation is amber) so admins can't confuse the two.
 *   - Shows "ADMIN VIEW" instead of "Viewing as {user}", because the admin
 *     is signed in as themselves, not as the customer.
 *   - Exit routes back to the customer's Owner Admin detail page.
 */
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { clearSessionToken, apiFetch } from '../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminViewBanner = () => {
  const { user } = useAuth();
  if (!user?.admin_view) return null;

  const wsId = user.admin_view.workspace_id;

  const exitAdminView = async () => {
    try {
      await apiFetch(`${API_URL}/api/auth/admin-view/end`, { method: 'POST' });
    } catch { /* best effort */ }
    clearSessionToken();
    // Send the admin back to the customer's detail page in Owner Admin so
    // they can pick their next action (impersonate, ticket, etc.).
    window.location.href = wsId ? `/owner-admin/customers/${wsId}` : '/owner-admin';
  };

  return (
    <div
      data-testid="admin-view-banner"
      className="fixed bottom-0 inset-x-0 z-[100] bg-red-600 text-white px-4 py-2 flex items-center justify-center gap-3 text-sm font-medium shadow-[0_-4px_16px_rgba(0,0,0,0.4)]"
    >
      <ShieldAlert size={16} className="shrink-0" />
      <span className="truncate">
        <strong>ADMIN VIEW</strong> — signed in as <strong>{user.email}</strong> · full write access · invisible to customer
      </span>
      <button
        onClick={exitAdminView}
        data-testid="admin-view-exit-btn"
        className="ml-2 px-3 py-1 rounded-full bg-black text-red-400 text-xs font-semibold hover:bg-black/80 transition-colors"
      >
        Exit
      </button>
    </div>
  );
};
